// ═══════════════════════════════════════════════════════════════
// GlobalCostIndicator — Indicateur coûts global dans la topbar
// Fetch /api/costs/global, refresh après chaque message
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Wallet, ChevronDown, ChevronUp } from 'lucide-react';
import { onGlobalCostUpdate } from '../../hooks/useStreamChat';

const formatCost = (usd) => {
    if (!usd || usd === 0) return '$0.00';
    if (usd < 0.01) return `$${usd.toFixed(4)}`;
    if (usd < 1) return `$${usd.toFixed(3)}`;
    return `$${usd.toFixed(2)}`;
};

const SERVICE_LABELS = {
    llm: 'LLM',
    embedding: 'Embeddings',
    whisper: 'Whisper',
    supadata: 'Supadata',
};

const GlobalCostIndicator = () => {
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const panelRef = useRef(null);

    const fetchGlobalCost = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('oracle_token');
            const res = await fetch('/api/costs/global', {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.ok) {
                const json = await res.json();
                setData(json);
            }
        } catch (e) {
            // Silently fail
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch on mount + every 60s
    useEffect(() => {
        fetchGlobalCost();
        const interval = setInterval(fetchGlobalCost, 60000);
        return () => clearInterval(interval);
    }, [fetchGlobalCost]);

    // Refresh on any LLM cost event
    useEffect(() => {
        return onGlobalCostUpdate(() => {
            setTimeout(fetchGlobalCost, 1500); // Slight delay for DB write
        });
    }, [fetchGlobalCost]);

    // Close panel on outside click
    useEffect(() => {
        if (!expanded) return;
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setExpanded(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [expanded]);

    const totalCost = data?.total_cost_usd || 0;

    return (
        <div ref={panelRef} style={{ position: 'relative' }}>
            {/* Pill button */}
            <button
                onClick={() => setExpanded(!expanded)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    background: 'transparent',
                    border: '1px solid var(--card-border)',
                    borderRadius: 999,
                    fontSize: '11px',
                    fontWeight: 500,
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    fontFamily: "'Inter', monospace",
                    letterSpacing: '-0.2px',
                }}
                title={`Coûts aujourd'hui: ${formatCost(totalCost)}`}
            >
                <Wallet size={12} style={{ color: 'var(--text-muted)' }} />
                <span>{formatCost(totalCost)}</span>
                {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>

            {/* Dropdown panel */}
            {expanded && data && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '6px',
                    width: '280px',
                    background: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '8px',
                    boxShadow: 'var(--shadow-lg)',
                    padding: '12px',
                    zIndex: 1000,
                    fontSize: '11px',
                    color: 'var(--text)',
                }}>
                    {/* Header */}
                    <div style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        marginBottom: '10px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid var(--card-border)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <span>Budget aujourd'hui</span>
                        <span style={{ fontFamily: 'monospace', color: '#22c55e', fontWeight: 700 }}>
                            {formatCost(totalCost)}
                        </span>
                    </div>

                    {/* By service */}
                    {data.by_service && Object.keys(data.by_service).length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Par service
                            </div>
                            {Object.entries(data.by_service).map(([svc, cost]) => (
                                <div key={svc} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                    <span>{SERVICE_LABELS[svc] || svc}</span>
                                    <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{formatCost(cost)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* By project */}
                    {data.by_project && data.by_project.length > 0 && (
                        <div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Par projet
                            </div>
                            {data.by_project.map((p) => (
                                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                    <span style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {p.name}
                                    </span>
                                    <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{formatCost(p.cost)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Events count */}
                    <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid var(--card-border)',
                        fontSize: '10px',
                        color: 'var(--text-muted)',
                        textAlign: 'center',
                    }}>
                        {data.event_count} appels trackés
                        {loading && ' · chargement...'}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalCostIndicator;
